import * as constants from "./constants.js";
import * as gsf from "./game-state-functions.js";
import * as shapeSpatialRelations from "./geometry/shape-spatial-relations.js";
import * as shapeModifierI from "./geometry/shape-modifier-immutable.js";
import * as random from "./util/random.js";
import clone from "./util/clone.js";
import forEach from "./util/for-each.js";
import * as trajectoryUtil from "./geometry/trajectory/trajectory-util.js";
import * as ShapeToGridConverter from "./geometry/shape-to-grid-converter.js";
var shapeToGridConverter = ShapeToGridConverter.createShapeToGridConverter();

import * as speedEffectDefinition from "./power-up/effect-definitions/speed.js";
import * as sizeEffectDefinition from "./power-up/effect-definitions/size.js";
import * as turningSpeedEffectDefinition from "./power-up/effect-definitions/turning-speed.js";
import * as wormSwitchEffectDefinition from "./power-up/effect-definitions/worm-switch.js";
import * as drunkEffectDefinition from "./power-up/effect-definitions/drunk.js";
import * as clearEffectDefinition from "./power-up/effect-definitions/clear.js";
import * as superJumpEffectDefinition from "./power-up/effect-definitions/super-jump.js";
import * as tronTurnEffectDefinition from "./power-up/effect-definitions/tron-turn.js";
import * as twinEffectDefinition from "./power-up/effect-definitions/twin.js";
import * as wallHackEffectDefinition from "./power-up/effect-definitions/wall-hack.js";
import * as switchSlowEffectDefinition from "./power-up/effect-definitions/switch-slow.js";
import { getCellStamp, setCellStamp } from "./util/cell-stamps.js";
import * as respawn from "./respawn.js";

var effectDefinitions = {};
effectDefinitions[speedEffectDefinition.type] = speedEffectDefinition;
effectDefinitions[sizeEffectDefinition.type] = sizeEffectDefinition;
effectDefinitions[turningSpeedEffectDefinition.type] = turningSpeedEffectDefinition;
effectDefinitions[wormSwitchEffectDefinition.type] = wormSwitchEffectDefinition;
effectDefinitions[drunkEffectDefinition.type] = drunkEffectDefinition;
effectDefinitions[clearEffectDefinition.type] = clearEffectDefinition;
effectDefinitions[superJumpEffectDefinition.type] = superJumpEffectDefinition;
effectDefinitions[tronTurnEffectDefinition.type] = tronTurnEffectDefinition;
effectDefinitions[twinEffectDefinition.type] = twinEffectDefinition;
effectDefinitions[wallHackEffectDefinition.type] = wallHackEffectDefinition;
effectDefinitions[switchSlowEffectDefinition.type] = switchSlowEffectDefinition;

// TEMP DEBUG (switcharoonie bring-up): when true, isFatalSegment never reports a crash, so no
// worm can die. Module-level so the single in-browser core bundle shared by the harness's C1/SV/C2
// all honour one flag. Set from the /debug/net harness; NEVER set in production.
var debugNoCrash = false;
function setDebugNoCrash(v) { debugNoCrash = !!v; }
function getDebugNoCrash() { return debugNoCrash; }

function computeWormSwitchAssignments(gameState) {
    return wormSwitchEffectDefinition.computeAssignments(gameState);
}

// The actual switcharoonie: swap owners (player-level) + slow everyone so they can find their new
// worm. `ownerMap` (from computeWormSwitchAssignments) lets the host apply the exact map it already
// shipped to clients; omit it and we compute one (offline instant path).
function applyWormSwitch(gameState, ownerMap) {
    if (ownerMap === undefined) { ownerMap = wormSwitchEffectDefinition.computeAssignments(gameState); }
    wormSwitchEffectDefinition.applyAssignments(gameState, ownerMap);
    gsf.getAliveWorms(gameState).forEach(function (worm) {
        var slowEffect = switchSlowEffectDefinition.activate({ wormId: worm.id });
        slowEffect.name = "switch_slow";
        slowEffect.time = gameState.gameTime;
        gsf.addEffect(gameState, slowEffect);
    });
}

function activatePowerUp(gameState, powerUpId, wormId, grabTime, grabTrackId) {
    var index = gameState.powerUps.findIndex(powerUp => powerUp.id === powerUpId);
    var powerUp = gameState.powerUps[index];
    // Switcharoonie never applies instantly - it runs the cinematic sequence: freeze everyone,
    // portals play out (SWITCH_ANIM_DURATION), swap + switch_slow at go.
    if (powerUp.effectType === "wormSwitch") {
        gsf.removePowerUp(gameState, powerUpId, grabTime, grabTrackId);
        // Only one player left with a living worm (a victory lap, a last survivor):
        // there is nobody to switch with, so it is spent and does nothing, like a
        // red power-up with no one to hit. Checked before the online branch too:
        // flagging a switch there froze the grabber waiting for a host sequence
        // that never comes (the victory lap has no host).
        var owners = {};
        gsf.getAliveWorms(gameState).forEach(function (w) { owners[w.playerId] = true; });
        if (Object.keys(owners).length < 2) {
            return;
        }
        // Online: client-authoritative worms can't be teleported to new owners mid-flight, and
        // the host must first confirm every client actually froze. Flag the request; the host
        // (match-runner) orchestrates freeze -> all-confirmed -> anim -> go and calls
        // applyWormSwitch at the go instant.
        //
        // WHO DRIVES THE SEQUENCE IS THE HOST'S TO SAY, NOT SOMETHING TO GUESS FROM THE
        // WORMS. A host that broadcasts to clients sets gameState.hostDrivenSwitch (the match
        // runner does, for every round it runs), and then the switch is always handed to it.
        // This was guessed from the worms three times and came back as a bug each time:
        //   - "any ALIVE worm is client-controlled" failed once every human was dead;
        //   - "any worm is client-controlled" failed when no human has a worm in the round at
        //     all: everyone watching a Royale stage they were cut from, a room of only fill
        //     seats with someone spectating. The swap happened with no freeze or go message,
        //     so the watching clients saw worms change hands with no animation.
        // People watch games they have no worm in. Do not infer "online" from the roster.
        // (The worm test stays only as a fallback for a host that does not set the flag.)
        if (gameState.hostDrivenSwitch || gameState.worms.some(function (w) { return w.externallyControlled; })) {
            gameState.pendingWormSwitch = { grabTime: grabTime };
            return;
        }
        // Local play (offline / all-bots): the same sequence, minus the confirmation wait (no
        // network - everyone freezes this instant). switchFreezeAt stands every worm still
        // (see updateWorms), switchAnim drives the portal animation + sounds on the client,
        // and updateEffects applies the swap at go.
        var assignments = wormSwitchEffectDefinition.computeAssignments(gameState);
        if (assignments) {
            var goTime = gameState.gameTime + constants.SWITCH_ANIM_DURATION;
            gameState.switchFreezeAt = gameState.gameTime;
            gameState.localSwitchSeq = { goTime: goTime, assignments: assignments };
            gameState.switchAnim = { freezeTime: gameState.gameTime, goTime: goTime, assignments: assignments };
        }
        return;
    }
    var effect = effectDefinitions[powerUp.effectType].activate({
        gameState,
        strength: powerUp.effectStrength,
        duration: powerUp.effectDuration,
        wormId,
        affects: powerUp.affects
    });
    if (effect) {
        effect.name = powerUp.name;
        effect.time = gameState.gameTime;

        if (powerUp.affects === "all") {
            // "all" means EVERY worm, which includes the grabber's OWN other worms (a twin) - not
            // just self + enemies. Splitting it into self + getAliveEnemyWorms (as this used to)
            // skipped the grabber's twin, so a blue power-up like wall-hack never reached it and it
            // crashed. Apply to every alive worm, cloning the effect per worm.
            gsf.getAliveWorms(gameState).forEach(function (worm) {
                var clonedEffect = clone(effect);
                clonedEffect.wormId = worm.id;
                gsf.addEffect(gameState, clonedEffect);
            });
        } else {
            if (powerUp.affects === "self") {
                gsf.addEffect(gameState, effect);
            }
            if (powerUp.affects === "others") {
                // The debuff is CARRIED BY A MISSILE, and the missile is REAL: it launches
                // from the token and pursues each victim's live head at RED_MISSILE_SPEED,
                // applying the effect on the actual catch (updateMissiles below). One
                // deterministic simulation for everyone - the server and offline play both
                // run this - so fleeing a missile genuinely buys time, and the visual
                // landing IS the activation. A victim that dies mid-flight fizzles its
                // missile. Red is a projectile; distance and evasion are the counterplay.
                gsf.getAliveEnemyWorms(gameState, wormId).forEach(function (worm) {
                    var clonedEffect = clone(effect);
                    clonedEffect.wormId = worm.id;
                    if (!gameState.missiles) { gameState.missiles = []; }
                    gameState.missiles.push({
                        id: powerUpId + "_" + worm.id,
                        x: powerUp.shape.centerX,
                        y: powerUp.shape.centerY,
                        radius: powerUp.shape.radius,
                        targetWormId: worm.id,
                        effect: clonedEffect
                    });
                })
            }
        }
    }

    gsf.removePowerUp(gameState, powerUpId, grabTime, grabTrackId);
}

function getRandomPositionInsidePlayableArea(gameState, minDistance) {
    var map = gameState.map;

    function intersectsBlockingShapes(shape) {
        for (var i = 0; i < map.blockingShapes.length; i++) {
            if (shapeSpatialRelations.intersects(map.blockingShapes[i], shape)) {
                return true;
            }
        }
        return false;
    }

    minDistance = minDistance || 0;
    var pos = {};
    var i = 0;
    while (i < 100000) {
        pos.x = random.random(gameState) * map.width;
        pos.y = random.random(gameState) * map.height;
        var shape = {centerX: pos.x, centerY: pos.y, radius: minDistance};
        if (shapeSpatialRelations.contains(map.shape, shape) && !intersectsBlockingShapes(shape)) {
            return pos;
        }
        i++;
    }
    throw Error("Failed to find a position inside playable area for the given shape");
}

function getWormDirection(gameState, wormId) {
    return transformValueUsingEffects(gameState, wormId, gsf.getWorm(gameState, wormId).direction, "changeDirection");
}

function getWormRadius(gameState, wormId) {
    var newSize = transformValueUsingEffects(gameState, wormId, gsf.getWorm(gameState, wormId).radius, "changeWormRadius");
    return Math.max(1, newSize);
}

function getWormSpeed(gameState, wormId) {
    var newSpeed = transformValueUsingEffects(gameState, wormId, gsf.getWorm(gameState, wormId).speed, "changeSpeed");
    return Math.max(5, newSpeed);
}

function getWormTurningSpeed(gameState, wormId) {
    var worm = gsf.getWorm(gameState, wormId);
    var speedUp = getWormSpeed(gameState, wormId) / worm.speed;
    var initValue = worm.turningSpeed * speedUp;
    return Math.min(5, transformValueUsingEffects(gameState, wormId, initValue, "changeTurningSpeed"));
}

function getWormTurningVelocity(gameState, wormId) {
    var turningVelocity;
    var player = gsf.getPlayer(gameState, gsf.getWorm(gameState, wormId).playerId);
    var tronTurnEffects = gsf.getWormEffects(gameState, wormId, "tronTurn");
    if (tronTurnEffects.length > 0) {
        turningVelocity = effectDefinitions["tronTurn"].getWormTurningVelocity(gameState, tronTurnEffects[0]);
    } else {
        turningVelocity = player.steering * getWormTurningSpeed(gameState, wormId);
    }
    return transformValueUsingEffects(gameState, wormId, turningVelocity, "changeTurningVelocity");
}

function isWormJumping(gameState, wormId) {
    return transformValueUsingEffects(gameState, wormId, gsf.getWorm(gameState, wormId).jump.remainingJumpTime > 0, "changeIsJumping");
}

function killPlayer(gameState, playerId) {
    var player = gsf.getPlayer(gameState, playerId);
    player.alive = false;
    gameState.gameEvents.push({
        type: "player_died",
        time: gameState.gameTime,
        playerId: playerId
    });
    // A Respawn round: their colour leaves the board and they are back in soon
    if (gameState.respawn) {
        respawn.onPlayerDied(gameState, playerId);
    }
}

function killWorm(gameState, wormId, fatal) {
    var worm = gsf.getWorm(gameState, wormId);
    worm.alive = false;
    // A "worm_died" game event for EACH worm crash (a player can own more than one
    // after a twin). This drives the crash SFX - one sound per worm wipeout - so a
    // twin clone crashing still sounds even while the player keeps playing. Distinct
    // from the "player_died" event below, which fires only once the player's LAST
    // worm is gone and drives scoring / being marked out.
    // `fatal` ({cause, killerTrackId}, optional) attributes the death: cause is
    // "wall" | "trail", killerTrackId is the trail's painter. The killer's playerId
    // is resolved NOW (ownership can move mid-round via switcharoonie).
    var event = {
        type: "worm_died",
        time: gameState.gameTime,
        wormId: wormId,
        playerId: worm.playerId
    };
    if (fatal && fatal.cause) {
        event.cause = fatal.cause;
        if (fatal.killerTrackId) {
            event.killerTrackId = fatal.killerTrackId;
            for (var ki = 0; ki < gameState.worms.length; ki++) {
                if (gameState.worms[ki].trackId === fatal.killerTrackId) {
                    event.killerPlayerId = gameState.worms[ki].playerId;
                    break;
                }
            }
            // A Respawn round credits the colour of the trail that was hit (after a
            // Switcharoonie a track holds several), see respawn.js roundScore
            if (gameState.respawn && fatal.debug && fatal.debug.cell) {
                event.trailPlayerId = respawn.trailPlayerAt(gameState, fatal.killerTrackId, fatal.debug.cell.index);
            }
        }
    }
    if (fatal && fatal.debug) {
        event.debug = fatal.debug;
    }
    if (fatal && fatal.via) {
        event.via = fatal.via; // e.g. "client_report": the client's own collision called it
    }
    gameState.gameEvents.push(event);
    addWormPathSegment(gameState, createWormPathSegmentWormDied(gameState, wormId));

    if (gsf.getAliveWorms(gameState, worm.playerId).length === 0) {
        killPlayer(gameState, worm.playerId);
    }
}

/**
 * Transform the given initValue based on effects owned by wormId. Each effect owned by wormId that has the function effectFunctionName in its definition will be called and the value will be changed in a pipe-line fashion.
 * Available effectFunctionNames: "changeSpeed", "changeTurningSpeed", "changeWormRadius"
 */
function transformValueUsingEffects(gameState, wormId, initValue, effectFunctionName) {
    /**
     * Returns all effects currently owned by wormId and that has the function effectFunctionName in its definition
     */
    function getEffectsWithFunction(gameState, wormId, effectFunctionName) {
        return gsf.getWormEffects(gameState, wormId).filter(e => effectDefinitions[e.type][effectFunctionName]);
    }

    return getEffectsWithFunction(gameState, wormId, effectFunctionName).reduce(function (accValue, effect) {
        var effectHandler = effectDefinitions[effect.type];
        return effectHandler[effectFunctionName](gameState, effect, accValue);
    }, initValue);
}

/**
 * Update what should happen on collision. Either on wall, worm or on power up
 */
// Does `segment`'s endpoint leave the map? (worm hit a wall)
function wormMapCollision(gameState, segment) {
    return !shapeSpatialRelations.contains(gameState.map.shape, {centerX: segment.endX, centerY: segment.endY, radius: segment.size});
}

// Does `segment`'s endpoint overlap a painted trail cell that isn't `worm`'s own
// recent (immune) trail? (worm hit a trail) Returns the killing cell's value (the
// painter worm's trackId) so the death can be attributed, or null if no collision.
function wormWormCollision(gameState, worm, segment) {
    var playArea = gameState.playArea;
    var cells = shapeToGridConverter.convert({centerX: segment.endX, centerY: segment.endY, radius: segment.size}, playArea, ShapeToGridConverter.RoundingModes.CONTAINMENT);
    // On a kill, return WHERE and WHY (which cell, its immunity stamp vs the worm's
    // travelled distance) so the death event carries enough forensics to tell an
    // honest crash from corrupted immunity bookkeeping.
    function fatalCellInfo(cell, value) {
        var stamp = getCellStamp(worm.distanceTravelledFromCells, cell);
        return {
            killerTrackId: value,
            cell: cell,
            col: cell % playArea.cols,
            row: Math.floor(cell / playArea.cols),
            stamp: stamp === undefined ? null : Math.round(stamp * 10) / 10,
            dist: Math.round(worm.distanceTravelled * 10) / 10,
            immunity: constants.IMMUNITY_DISTANCE_MULTIPLIER * segment.size
        };
    }
    for (var ci = 0; ci < cells.length; ci++) {
        var cell = cells[ci];
        var value = playArea.grid[cell];
        if (value !== constants.PLAY_AREA_FREE) { // TODO Utility function to check if worm-id
            if (!(worm.distanceTravelled - getCellStamp(worm.distanceTravelledFromCells, cell) <= constants.IMMUNITY_DISTANCE_MULTIPLIER * segment.size)) {
                var twinEffects = gsf.getWormEffects(gameState, worm.id, "twin");
                if (twinEffects.length > 0) {
                    var immuneTwins = twinEffects.map(te => gsf.getWorm(gameState, te.twinWormId))
                            .filter(function (twinWorm) {
                                return (twinWorm.distanceTravelled - getCellStamp(twinWorm.distanceTravelledFromCells, cell) <= constants.IMMUNITY_DISTANCE_MULTIPLIER * segment.size)
                            });
                    if (immuneTwins.length === 0) {
                        return fatalCellInfo(cell, value);
                    }
                    continue;
                }
                return fatalCellInfo(cell, value);
            }
        }
    }
    return null;
}

// Compact snapshot of the segment + worm state at a kill, for the worm_died
// event's debug payload (reload/desync forensics: is the fatal segment sane, was
// the worm sim-driven, do the times line up with its geometry).
function fatalSegmentDebug(worm, segment) {
    var r1 = function (v) { return typeof v === "number" ? Math.round(v * 1000) / 1000 : v; };
    return {
        seg: {
            type: segment.type,
            startTime: r1(segment.startTime),
            endTime: r1(segment.endTime),
            duration: r1(segment.duration),
            speed: r1(segment.speed),
            size: r1(segment.size),
            jump: !!segment.jump,
            startX: r1(segment.startX), startY: r1(segment.startY),
            endX: r1(segment.endX), endY: r1(segment.endY)
        },
        worm: {
            dist: r1(worm.distanceTravelled),
            x: r1(worm.centerX), y: r1(worm.centerY),
            ext: !!worm.externallyControlled
        }
    };
}

// True if `segment` (a worm's head move) is a fatal collision - wall or trail -
// honouring wallHack (walls) and jump (no trail collision while holing). Shared by
// the per-tick updateCollision and the per-adopted-segment check so a
// client-authoritative worm can't tunnel a crossing between ticks.
function isFatalSegment(gameState, worm, segment) {
    // TEMP DEBUG (switcharoonie bring-up): globally disable all crashes so worms can never die.
    // Lets us watch whether they resume from the correct spots after a switch without a crash
    // masking the result. Toggled from the /debug/net harness (?nocrash=1); off in production.
    // gameState.noCrash is the same switch for ONE match (the edge-server's dev-only perf
    // bench sets it per room), so it can't leak into other rooms sharing the isolate.
    if (debugNoCrash || gameState.noCrash) {
        return false;
    }
    // A stationary worm can't crash: the start phase (still_arc) and a switcharoonie freeze (a
    // relabelled speed-0 "straight" segment) both hold in place, so nothing to collide into.
    if (!worm.alive || segment.type === "still_arc" || segment.speed === 0) {
        return false;
    }
    // Walls kill even mid-jump: a jump / super-jump holes over TRAILS, it is not a wall pass. Only
    // wallHack lets you through a wall, so the map-collision check runs regardless of jump.
    if (!gsf.hasWormEffect(gameState, worm.id, wallHackEffectDefinition.type) && wormMapCollision(gameState, segment)) {
        return {cause: "wall", debug: fatalSegmentDebug(worm, segment)};
    }
    if (segment.jump) {
        return false; // holing: skip only the trail collision below
    }
    var hit = wormWormCollision(gameState, worm, segment);
    if (hit === null) {
        return false;
    }
    var debug = fatalSegmentDebug(worm, segment);
    debug.cell = {index: hit.cell, col: hit.col, row: hit.row, stamp: hit.stamp, dist: hit.dist, immunity: hit.immunity};
    return {cause: "trail", killerTrackId: hit.killerTrackId, debug: debug};
}

function updateCollision(gameState) {
    function wormPowerUpCollision(gameState, segment) {
        var powerUps = gameState.powerUps;
        var collidedPowerUps = [];
        powerUps.forEach(function (powerUp) {
            if (shapeSpatialRelations.intersects({centerX: segment.endX, centerY: segment.endY, radius: segment.size}, powerUp.shape)) {
                collidedPowerUps.push(powerUp.id);
            }
        });
        return collidedPowerUps;
    }

    gsf.forEachAliveLatestWormPathSegment(gameState, function (segment) {
        var worm = gsf.getWorm(gameState, segment.wormId);
        wormPowerUpCollision(gameState, segment).forEach(function (powerUpId) {
            // Stamp the despawn with the game-time the head actually crossed the power-up
            // (the segment's geometric end), not gameState.gameTime. For a client-
            // authoritative worm the host adopts this segment ~half-RTT after the client
            // grabbed it, so gameState.gameTime is that much too late - which made the
            // power-up linger on the OTHER client's screen after the grabbing worm passed
            // it. startTime+duration is the real end even when the host stamps endTime
            // forward each tick to keep collision running.
            activatePowerUp(gameState, powerUpId, worm.id, segment.startTime + (segment.duration || 0), worm.trackId);
        });
        var fatal = isFatalSegment(gameState, worm, segment);
        if (fatal) {
            killWorm(gameState, worm.id, fatal);
        }
    });
}

function createWormPathSegment(gameState, wormId, {duration}) {
    var worm = gsf.getWorm(gameState, wormId);
    var direction = getWormDirection(gameState, wormId);
    var size = getWormRadius(gameState, wormId);
    var turningVelocity = getWormTurningVelocity(gameState, wormId);
    var jump = isWormJumping(gameState, wormId);
    var speed = gsf.isInStartPhase(gameState) ? 0 : getWormSpeed(gameState, wormId);

    return gsf.createWormPathSegment(gameState, wormId, {duration, centerX: worm.centerX, centerY: worm.centerY, direction, speed, turningVelocity, jump, size});
}


function createWormPathSegmentWormDied(gameState, wormId) {
    var wps = createWormPathSegment(gameState, wormId, {duration: 0});
    wps.type = "worm_died";
    return wps;
}

function addWormPathSegment(gameState, wormPathSegment) {
    gsf.addWormPathSegment(gameState, wormPathSegment);

    if (gsf.hasWormEffect(gameState, wormPathSegment.wormId, "wallHack")) {
        wallHackEffectDefinition.updateWorm(gameState, wormPathSegment.duration, wormPathSegment.wormId, wormPathSegment);
    }
}

// Fun-stat bookkeeping (drives the "Gaps threaded" match stat): a "gap" is trail
// or wall hugging the head on BOTH sides; entering then leaving one counts as a
// thread. Pure per-worm bookkeeping (worm.inGap / worm.gapsThreaded), never
// streamed and never touching collision logic.
function updateGapThreading(gameState, worm, segment) {
    if (segment.speed === 0 || gameState.victoryLap || gsf.isInStartPhase(gameState)) {
        worm.inGap = false;
        return;
    }
    function isSolidAt(x, y) {
        if (!shapeSpatialRelations.contains(gameState.map.shape, {centerX: x, centerY: y, radius: 0.5})) {
            return true; // outside the map: the wall makes a gap side too
        }
        var playArea = gameState.playArea;
        var row = Math.round(y);
        var col = Math.round(x);
        if (row < 0 || col < 0 || row >= playArea.rows || col >= playArea.cols) {
            return true;
        }
        var cell = row * playArea.cols + col;
        if (playArea.grid[cell] === constants.PLAY_AREA_FREE) {
            return false;
        }
        // The worm's own collision-immune fresh trail right behind the head is not a
        // gap side, or every tight turn would count as a thread.
        var stamp = getCellStamp(worm.distanceTravelledFromCells, cell);
        return !(stamp !== undefined
                && worm.distanceTravelled - stamp <= constants.IMMUNITY_DISTANCE_MULTIPLIER * segment.size);
    }
    var direction = segment.endDirection !== undefined ? segment.endDirection : worm.direction;
    var probe = segment.size * 2.2;
    var px = Math.cos(direction + Math.PI / 2) * probe;
    var py = Math.sin(direction + Math.PI / 2) * probe;
    var inGap = isSolidAt(segment.endX + px, segment.endY + py) && isSolidAt(segment.endX - px, segment.endY - py);
    if (inGap && !worm.inGap) {
        worm.inGap = true;
    } else if (!inGap && worm.inGap) {
        worm.inGap = false;
        worm.gapsThreaded = (worm.gapsThreaded || 0) + 1;
    }
}

/**
 * Updates each worms
 */
function updateWorms(gameState, deltaTime) {
    gsf.forEachAliveWorm(gameState, function (worm) {
        // Externally controlled worms (e.g. an online human, whose movement is
        // authoritative on their own client) are advanced by the host applying the
        // client's path segments, not simulated here. Collision still runs for them.
        if (worm.externallyControlled) {
            return;
        }
        // Switcharoonie freeze (online host): between the freeze instant and go the whole
        // world stands still. Client-owned worms stop on their own clients; the worms the
        // host simulates (bots) stop here the same way: no movement, no segments - a time
        // gap in the track, exactly like the client-owned ones. The host sets/clears
        // switchFreezeAt around the sequence (see match-runner handleWormSwitch).
        if (gameState.switchFreezeAt !== null && gameState.switchFreezeAt !== undefined && gameState.gameTime >= gameState.switchFreezeAt) {
            return;
        }
        var wormPathSegment = createWormPathSegment(gameState, worm.id, {duration: deltaTime});

        //TODO: Will get removed when we no longer have collision detection based on playArea
        worm.direction += wormPathSegment.turningVelocity * deltaTime;
        worm.centerX = wormPathSegment.endX;
        worm.centerY = wormPathSegment.endY;
        worm.distanceTravelled += wormPathSegment.speed * deltaTime;

        // No body update during the start phase and also only render the body if we are not standing still
        if (getWormSpeed(gameState, worm.id) > 0 && !isWormJumping(gameState, worm.id)) {
            gsf.addPlayAreaShape(gameState, {centerX: wormPathSegment.startX, centerY: wormPathSegment.startY, radius: wormPathSegment.size}, wormPathSegment.id).forEach(function (cell) {
                setCellStamp(worm, cell.index, worm.distanceTravelled);
            });
        }

        addWormPathSegment(gameState, wormPathSegment);
        updateGapThreading(gameState, worm, wormPathSegment);
    });
}

// Apply a worm-path segment whose movement was computed elsewhere (an online
// human's own client). Mirrors updateWorms for one worm: paints the trail at the
// segment START (so the head always leads the paint by a step, which is what lets a
// worm die crossing a trail), advances the worm's distance bookkeeping for
// self-immunity, and adds the segment. The normal updateCollision pass then detects
// any wall/worm collision against the segment's head. Jump segments leave no trail
// (the hole). NOTE: the client must send SHORT segments (a few px each) so the
// per-start paint forms a continuous trail.
function applyExternalWormSegment(gameState, wormId, seg) {
    var worm = gsf.getWorm(gameState, wormId);
    if (!worm || !worm.alive) {
        return;
    }
    worm.distanceTravelled += seg.speed * seg.duration;
    if (seg.speed > 0 && !seg.jump && seg.type !== "still_arc") {
        gsf.addPlayAreaShape(gameState, {centerX: seg.startX, centerY: seg.startY, radius: seg.size}, seg.id).forEach(function (cell) {
            setCellStamp(worm, cell.index, worm.distanceTravelled);
        });
    }
    worm.direction = seg.endDirection;
    worm.centerX = seg.endX;
    worm.centerY = seg.endY;
    addWormPathSegment(gameState, seg);
    updateGapThreading(gameState, worm, seg);
    // Collision-check THIS segment now. The per-tick updateCollision only checks a
    // worm's LATEST segment, but an externally-controlled (client-authoritative) worm
    // adopts a whole batch of segments per network message - so an intermediate
    // crossing (into a wall or another worm's trail) would be skipped and the worm
    // would tunnel straight through. Checking each adopted segment kills it at the
    // real crossing point instead.
    var fatal = isFatalSegment(gameState, worm, seg);
    if (fatal) {
        killWorm(gameState, worm.id, fatal);
    }
}

function updateEffects(gameState, deltaTime) {
    // Local switcharoonie sequence (see activatePowerUp): the world froze at the grab; once
    // the portal animation has played out, apply the swap + switch_slows and release the freeze.
    if (gameState.localSwitchSeq && gameState.gameTime >= gameState.localSwitchSeq.goTime) {
        applyWormSwitch(gameState, gameState.localSwitchSeq.assignments);
        gameState.localSwitchSeq = null;
        gameState.switchFreezeAt = null;
    }
    // Red-missile deliveries in flight: each missile pursues its victim's live head at
    // constant RED_MISSILE_SPEED and applies its debuff on the ACTUAL catch (see
    // activatePowerUp's "others" branch). The effect is stamped with the current game
    // time so its event, timer ring and timeLeft all start at the landing instant.
    // A victim that dies mid-flight fizzles its missile.
    var missiles = gameState.missiles;
    if (missiles && missiles.length) {
        for (var mi = missiles.length - 1; mi >= 0; mi--) {
            var m = missiles[mi];
            var victim = gsf.getWorm(gameState, m.targetWormId);
            if (!victim || !victim.alive) {
                missiles.splice(mi, 1);
                continue;
            }
            var mdx = victim.centerX - m.x;
            var mdy = victim.centerY - m.y;
            var mdist = Math.hypot(mdx, mdy);
            var mstep = constants.RED_MISSILE_SPEED * deltaTime;
            if (mdist <= mstep + getWormRadius(gameState, victim.id)) {
                m.effect.time = gameState.gameTime;
                gsf.addEffect(gameState, m.effect);
                missiles.splice(mi, 1);
            } else {
                m.x += (mdx / mdist) * mstep;
                m.y += (mdy / mdist) * mstep;
            }
        }
    }
    var effects = gameState.effects;
    for (var i = effects.length - 1; i >= 0; i--) {
        var effect = effects[i];
        effect.timeLeft -= deltaTime;
        var effectDefinition = effectDefinitions[effect.type];
        if (effectDefinition.update) {
            effectDefinition.update(gameState, deltaTime, effect)
        }
        if (effect.timeLeft <= 0) {
            gsf.removeEffect(gameState, effect.id);
        }
    }
}

function updatePlayers(gameState, deltaTime) {
    gameState.players.forEach(function (player) {
        gsf.addPlayerSteeringSegment(gameState, player.id, player.steering, deltaTime);
    });
}

function updatePowerUps(gameState, deltaTime) {
    function attemptGetPowerUpWithRandomPos(powerUp) {
        function isCollidingWithWorms(shape) {
            for (var i in gameState.worms) {
                var worm = gameState.worms[i];
                if (shapeSpatialRelations.intersects(worm, shape)) {
                    return true;
                }
            }
            return false;
        }

        function isCollidingWithPowerUps(shape) {
            for (var i in gameState.powerUps) {
                var powerUp = gameState.powerUps[i];
                if (shapeSpatialRelations.intersects(powerUp.shape, shape)) {
                    return true;
                }
            }
            return false;
        }

        var position = getRandomPositionInsidePlayableArea(gameState, powerUp.shape.radius);
        powerUp.shape = shapeModifierI.setPosition(powerUp.shape, position.x - powerUp.shape.radius, position.y - powerUp.shape.radius);
        var MAX_POWER_UP_SPAWN_ATTEMPTS = 100;
        var counter = 0;
        while (isCollidingWithWorms(powerUp.shape) || isCollidingWithPowerUps(powerUp.shape)) {
            if (counter > MAX_POWER_UP_SPAWN_ATTEMPTS) {
                return undefined;
            }
            position = getRandomPositionInsidePlayableArea(gameState, powerUp.shape.radius);
            powerUp.shape = shapeModifierI.setPosition(powerUp.shape, position.x - powerUp.shape.radius, position.y - powerUp.shape.radius);
            counter++;
        }
        return powerUp;
    }

    function attemptSpawnRandomPowerUp() {
        // Effect types disabled for this game (e.g. online play disables effects that
        // don't yet work with client-authoritative worms). Empty for offline play.
        var disabledTypes = gameState.disabledEffectTypes || [];
        // Individual power-ups disabled for this game, by their powerUpDefinitions
        // key (e.g. "slow", "wall_hack_all"). Used by local play's settings panel.
        var disabledPowerUps = gameState.disabledPowerUps || [];
        function enabled(def, key) {
            return disabledTypes.indexOf(def.effectType) === -1 && disabledPowerUps.indexOf(key) === -1;
        }
        var totalSpawnWeight = 0;
        forEach(constants.powerUpDefinitions, function (powerUpDefinition, key) {
            if (enabled(powerUpDefinition, key)) { totalSpawnWeight += powerUpDefinition.weightedSpawnChance; }
        });
        if (totalSpawnWeight <= 0) { return; }
        var randomValue = random.random(gameState);
        var currentChance = 0;
        var found = false;
        forEach(constants.powerUpDefinitions, function (powerUpDefinition, key) {
            if (!enabled(powerUpDefinition, key)) { return; }
            currentChance += powerUpDefinition.weightedSpawnChance / totalSpawnWeight;
            if (!found && currentChance > randomValue) {
                found = true;
                var powerUp = attemptGetPowerUpWithRandomPos({
                    name: powerUpDefinition.name,
                    effectType: powerUpDefinition.effectType,
                    shape: clone(constants.POWER_UP_SHAPE),
                    effectStrength: powerUpDefinition.effectStrength,
                    effectDuration: powerUpDefinition.effectDuration,
                    affects: powerUpDefinition.affects
                });
                if (powerUp !== undefined) {
                    gsf.addPowerUp(gameState, powerUp);
                }
            }
        });
    }

    // Time-based chance to spawn a power-up (Linear ramp: guarantees one within
    // 1/chance seconds). The chance is per-game so local play's settings panel can
    // tune it; 0 disables spawning entirely. The accumulator lives on gameState so
    // it stays deterministic and per-game (the old module-level trigger leaked its
    // accumulated time across games).
    var spawnChance = gameState.powerUpSpawnChance;
    if (spawnChance === undefined) { spawnChance = constants.POWER_UP_SPAWN_CHANCE; }
    // No NEW power-ups during the cosmetic victory lap: the round is already scored, so a
    // fresh spawn just clutters the celebration. Power-ups already on the field stay pickable
    // (updateCollision still activates them), so a lap twin etc. keeps working.
    if (spawnChance > 0 && !gameState.victoryLap) {
        var acc = gameState.powerUpSpawnAccTime || 0;
        var currentSpawnChance = deltaTime / (1 / spawnChance - acc);
        gameState.powerUpSpawnAccTime = acc + deltaTime;
        if (currentSpawnChance >= random.random(gameState)) {
            gameState.powerUpSpawnAccTime = 0;
            attemptSpawnRandomPowerUp();
        }
    }
}

function updateWormJumps(gameState, deltaTime) {
    gsf.forEachAliveWorm(gameState, function (worm) {
        // Externally controlled worms own their own jumps (decided client-side).
        if (worm.externallyControlled) {
            return;
        }
        // Frozen for a switcharoonie (see updateWorms): jump timers pause with the worm.
        if (gameState.switchFreezeAt !== null && gameState.switchFreezeAt !== undefined && gameState.gameTime >= gameState.switchFreezeAt) {
            return;
        }
        if (worm.jump.remainingJumpTime <= 0) {
            worm.jump.timeSinceLastJump += deltaTime;
            if (worm.jump.timeSinceLastJump > constants.JUMP_COOLDOWN) {
                function startJumping() {
                    if (worm.speed > 0) {
                        worm.jump.remainingJumpTime =  (2 * getWormRadius(gameState, worm.id) + 4 * worm.radius) / worm.speed;
                    } else {
                        worm.jump.remainingJumpTime = 0;
                    }
                    worm.jump.timeSinceLastJump = 0;
                }

                var notJumpedElapsedTime = worm.jump.timeSinceLastJump - constants.JUMP_COOLDOWN;
                var notJumpedMaxTime = 1 / constants.JUMP_CHANCE;
                var jumpChance = deltaTime / (notJumpedMaxTime - (notJumpedElapsedTime - deltaTime));
                if(jumpChance >= random.random(gameState)) {
                    startJumping();
                }
            }
        } else {
            worm.jump.remainingJumpTime -= deltaTime;
        }
    });
}

export {
    activatePowerUp,
    applyWormSwitch,
    computeWormSwitchAssignments,
    applyExternalWormSegment,
    createWormPathSegment,
    createWormPathSegmentWormDied,
    getRandomPositionInsidePlayableArea,
    getWormDirection,
    getWormRadius,
    getWormSpeed,
    getWormTurningSpeed,
    getWormTurningVelocity,
    isWormJumping,
    fatalSegmentDebug,
    isFatalSegment,
    killPlayer,
    killWorm,
    updateCollision,
    updateWorms,
    updateEffects,
    updatePlayers,
    updatePowerUps,
    updateWormJumps,
    setDebugNoCrash,
    getDebugNoCrash
};
